import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage, GridReferenceConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { Compass, Crosshair } from 'lucide-react-native';

interface GridReferenceActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const GridReferenceActivity: React.FC<GridReferenceActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as GridReferenceConfig;
    const targets = useMemo(() => config.targets || [], [config.targets]);
    const dimensions = config.gridDimensions || { eastings: 5, northings: 5 };

    const targetToFind = useMemo(() => {
        return targets.find((t) => t.id === config.targetIdToFind) || targets[0] || null;
    }, [targets, config.targetIdToFind]);

    // Selected cell: { easting, northing }
    const [selectedCoord, setSelectedCoord] = useState<{ easting: number; northing: number } | null>(null);
    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [statusMessage, setStatusMessage] = useState<string>(
        config.prompt || 'Remember: along the corridor (Eastings), then up the stairs (Northings)!'
    );
    const [hasCompleted, setHasCompleted] = useState(false);

    const handleSelectCell = (e: number, n: number) => {
        if (hasCompleted) return;
        setSelectedCoord({ easting: e, northing: n });
        setTrafficStatus('idle');
        setStatusMessage(`Selected: (${e}, ${n}). Check your reference!`);
    };

    const handleCheck = () => {
        if (!selectedCoord || !targetToFind || hasCompleted) return;

        const isCorrect =
            selectedCoord.easting === targetToFind.coordinates.easting &&
            selectedCoord.northing === targetToFind.coordinates.northing;

        if (isCorrect) {
            setTrafficStatus('correct');
            setStatusMessage(`Pinpoint Accuracy! You found ${targetToFind.label} at grid reference (${targetToFind.coordinates.easting}, ${targetToFind.coordinates.northing}).`);
            setHasCompleted(true);

            useStrokeStore.getState().addCustomAnswer({
                activityId: activity.id,
                value: selectedCoord,
                mechanic: 'grid_reference',
                isCorrect: true,
                score: 100,
            });

            setTimeout(() => {
                onComplete();
            }, 1200);
        } else {
            setTrafficStatus('incorrect');
            setStatusMessage(`Not quite at ${targetToFind.label}. Remember: Eastings first (bottom axis), then Northings (side axis).`);
        }
    };

    return (
        <StageCard testID="grid-reference-card">
            <View style={styles.container}>
                {/* Header Row */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badge}>
                            <Compass size={14} color="#4F46E5" />
                            <Text style={styles.badgeText}>OS Grid Reference Navigator</Text>
                        </View>
                        <Text style={styles.promptText}>
                            {config.prompt || `Find the grid reference for: ${targetToFind?.label || 'Target'}`}
                        </Text>
                    </View>
                    <View style={styles.headerControls}>
                        {targetToFind && (
                            <View style={styles.targetBadge}>
                                <Text style={styles.targetIcon}>{targetToFind.icon}</Text>
                                <Text style={styles.targetName}>{targetToFind.label}</Text>
                            </View>
                        )}
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Grid Container with Eastings and Northings */}
                <View style={styles.mapGridWrapper}>
                    <View style={styles.gridOuter}>
                        {/* Rows: from top to bottom (northing descending) */}
                        {Array.from({ length: dimensions.northings }).map((_, rIdx) => {
                            const n = dimensions.northings - 1 - rIdx;
                            return (
                                <View key={`northing_row_${n}`} style={styles.mapRow}>
                                    {/* Northing Label on the left */}
                                    <View style={styles.axisLabelBox}>
                                        <Text style={styles.axisLabelText}>{n}</Text>
                                    </View>

                                    {/* Grid Cells across Eastings */}
                                    {Array.from({ length: dimensions.eastings }).map((_, e) => {
                                        const targetHere = targets.find(
                                            (t) => t.coordinates.easting === e && t.coordinates.northing === n
                                        );
                                        const isSelected =
                                            selectedCoord?.easting === e && selectedCoord?.northing === n;

                                        return (
                                            <TouchableOpacity
                                                key={`cell_${e}_${n}`}
                                                style={[
                                                    styles.mapCell,
                                                    isSelected && styles.mapCellSelected,
                                                    targetHere && styles.mapCellHasTarget,
                                                ]}
                                                onPress={() => handleSelectCell(e, n)}
                                                disabled={hasCompleted}
                                                accessibilityLabel={`Grid cell easting ${e} northing ${n}`}
                                                testID={`grid-cell-${e}-${n}`}
                                            >
                                                {targetHere && (
                                                    <Text style={styles.targetEmoji}>{targetHere.icon}</Text>
                                                )}
                                                {isSelected && (
                                                    <View style={styles.crosshairOverlay}>
                                                        <Crosshair size={20} color="#EF4444" />
                                                    </View>
                                                )}
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>
                            );
                        })}

                        {/* Bottom Eastings Row */}
                        <View style={styles.eastingsRow}>
                            <View style={styles.axisCornerPlaceholder} />
                            {Array.from({ length: dimensions.eastings }).map((_, e) => (
                                <View key={`easting_lbl_${e}`} style={styles.axisLabelBox}>
                                    <Text style={styles.axisLabelText}>{e}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                </View>

                {/* Selected Coordinate Display & Action */}
                <View style={styles.controlBar}>
                    <View style={styles.selectionPill}>
                        <Text style={styles.selectionPillLabel}>SELECTED GRID REF:</Text>
                        <Text style={styles.selectionPillValue}>
                            {selectedCoord ? `(${selectedCoord.easting}, ${selectedCoord.northing})` : 'None selected'}
                        </Text>
                    </View>

                    <JuicyButton
                        label="VERIFY REFERENCE"
                        variant="primary"
                        size="medium"
                        onPress={handleCheck}
                        disabled={hasCompleted || !selectedCoord}
                        testID="grid-check-button"
                    />
                </View>

                {/* Status Bar */}
                <View style={styles.statusBar}>
                    <Text style={styles.statusText}>{statusMessage}</Text>
                </View>
            </View>
        </StageCard>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    promptText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    targetBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F1F5F9',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 6,
    },
    targetIcon: {
        fontSize: 18,
    },
    targetName: {
        fontSize: 13,
        fontWeight: '800',
        color: '#334155',
    },
    mapGridWrapper: {
        alignItems: 'center',
        marginVertical: 4,
    },
    gridOuter: {
        borderWidth: 2,
        borderColor: '#94A3B8',
        borderRadius: 12,
        backgroundColor: '#F0FDF4', // Topographic contour tint
        overflow: 'hidden',
    },
    mapRow: {
        flexDirection: 'row',
    },
    mapCell: {
        width: 58,
        height: 58,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
    },
    mapCellSelected: {
        backgroundColor: '#FEF08A',
        borderColor: '#F59E0B',
    },
    mapCellHasTarget: {
        backgroundColor: '#FFFFFF',
    },
    targetEmoji: {
        fontSize: 26,
    },
    crosshairOverlay: {
        position: 'absolute',
    },
    axisLabelBox: {
        width: 32,
        height: 58,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#E2E8F0',
    },
    axisCornerPlaceholder: {
        width: 32,
        height: 28,
        backgroundColor: '#CBD5E1',
    },
    eastingsRow: {
        flexDirection: 'row',
        backgroundColor: '#E2E8F0',
        borderTopWidth: 2,
        borderTopColor: '#94A3B8',
    },
    axisLabelText: {
        fontSize: 13,
        fontWeight: '900',
        color: '#334155',
    },
    controlBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
    },
    selectionPill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 8,
    },
    selectionPillLabel: {
        fontSize: 11,
        fontWeight: '800',
        color: '#64748B',
    },
    selectionPillValue: {
        fontSize: 15,
        fontWeight: '900',
        color: '#0F172A',
    },
    statusBar: {
        backgroundColor: '#F1F5F9',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
    },
    statusText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        textAlign: 'center',
    },
});
